import { parse, validate } from '@tma.js/init-data-node'

function botToken(): string {
  const t = process.env.BOT_TOKEN
  if (!t) throw new Error('BOT_TOKEN is required')
  return t
}

export type TgUser = {
  id: number
  firstName?: string
  lastName?: string
  username?: string
  languageCode?: string
  isPremium?: boolean
  photoUrl?: string
}

export type TgInitData = {
  user?: TgUser
  startParam?: string
  authDate: Date
  chatInstance?: string
  chatType?: string
}

export function verifyInitData(raw: string): TgInitData {
  validate(raw, botToken(), { expiresIn: 3600 })
  const parsed = parse(raw)
  return parsed as unknown as TgInitData
}

export type StartParam =
  | { kind: 'quote'; groupId: string; localId: number }
  | { kind: 'group'; groupId: string }
  | { kind: 'game'; groupId: string }
  | { kind: 'top'; groupId: string }
  | { kind: 'settings'; groupId?: string }
  | { kind: 'root' }

// Accepts the payloads emitted by helpers/deep-link.js in the bot:
//   q_<local_id>_g_<objectId_hex>   — open a specific quote
//   g_<objectId_hex>                — open a group's feed
//   game_<objectId_hex>             — open a group's "who said it" game (challenge)
//   top_<objectId_hex>              — open a group's hall of fame
//   (empty / anything else)         — groups list
// groupId must look like a Mongo ObjectId (24 hex). localId must be a
// positive integer. Invalid payloads fall through to 'root' so a broken
// link still lands the user somewhere useful.
const OBJECT_ID_RE = /^[a-f0-9]{24}$/i

export function parseStartParam(sp: string | undefined | null): StartParam {
  if (!sp) return { kind: 'root' }
  const parts = sp.split('_')
  if (parts[0] === 'q' && parts[1] && parts[2] === 'g' && parts[3]) {
    const localId = Number(parts[1])
    if (Number.isInteger(localId) && localId > 0 && OBJECT_ID_RE.test(parts[3])) {
      return { kind: 'quote', localId, groupId: parts[3] }
    }
  }
  if (parts[0] === 'g' && parts[1] && OBJECT_ID_RE.test(parts[1])) {
    return { kind: 'group', groupId: parts[1] }
  }
  if (parts[0] === 'game' && parts[1] && OBJECT_ID_RE.test(parts[1])) {
    return { kind: 'game', groupId: parts[1] }
  }
  if (parts[0] === 'top' && parts[1] && OBJECT_ID_RE.test(parts[1])) {
    return { kind: 'top', groupId: parts[1] }
  }
  // `settings` = personal style, `settings_<groupId>` = the group's style.
  if (parts[0] === 'settings' && parts.length === 1) return { kind: 'settings' }
  if (parts[0] === 'settings' && parts[1] && OBJECT_ID_RE.test(parts[1])) {
    return { kind: 'settings', groupId: parts[1] }
  }
  return { kind: 'root' }
}
